"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Upload, FolderPlus, Globe2 } from "lucide-react";
import { UploadModal } from "@/components/UploadModal";
import { CreateProjectDialog } from "@/components/CreateProjectDialog";
import { fetchFromApi } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface QuickActionCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
    variant?: "default" | "primary";
    badge?: string;
}

function QuickActionCard({ icon, title, description, onClick, variant = "default", badge }: QuickActionCardProps) {
    return (
        <button
            onClick={onClick}
            className={cn(
                "group relative flex flex-col items-start gap-3 p-5 rounded-xl border-2 transition-all duration-200 text-left w-full",
                "hover:shadow-md hover:-translate-y-0.5",
                variant === "primary"
                    ? "border-primary/20 bg-primary/5 hover:border-primary/40 hover:bg-primary/10"
                    : "border-border/50 bg-card hover:border-border hover:bg-accent/50"
            )}
        >
            {badge && (
                <span className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    {badge}
                </span>
            )}
            <div className={cn(
                "flex items-center justify-center w-10 h-10 rounded-lg transition-colors",
                variant === "primary"
                    ? "bg-primary/10 text-primary group-hover:bg-primary/20"
                    : "bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-foreground"
            )}>
                {icon}
            </div>
            <div>
                <h3 className="font-semibold text-foreground">{title}</h3>
                <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
            </div>
        </button>
    );
}

interface QuickActionsProps {
    onUploadComplete?: () => void;
    onProjectCreated?: () => void;
    onUploadStart?: (files: File[]) => void;
    onUrlImportStart?: (url: string) => void;
    isUploadBlocked?: boolean;
    onUploadBlocked?: () => void;
}

export function QuickActions({ onUploadComplete, onProjectCreated, onUploadStart, onUrlImportStart, isUploadBlocked, onUploadBlocked }: QuickActionsProps) {
    const router = useRouter();
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [isCreateProjectOpen, setCreateProjectOpen] = useState(false);

    const handleUploadClick = () => {
        if (isUploadBlocked && onUploadBlocked) {
            onUploadBlocked();
        } else {
            setUploadModalOpen(true);
        }
    };

    const handleUploadComplete = (paperId: string) => {
        router.push(`/paper/${paperId}`);
        onUploadComplete?.();
    };

    const handleCreateProject = async (title: string, description: string) => {
        try {
            const response = await fetchFromApi("/api/projects", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ title, description }),
            });

            if (response?.id) {
                try {
                    await router.push(`/projects/${response.id}`);
                    toast.success("Project created successfully!");
                    setCreateProjectOpen(false);
                    onProjectCreated?.();
                } catch (navError) {
                    console.error("Navigation error:", navError);
                    toast.error("Project created, but failed to navigate. Please try again.");
                }
            } else {
                toast.error("Failed to create project. Please try again.");
            }
        } catch (error) {
            console.error("Error creating project:", error);
            toast.error("Failed to create project. Please try again.");
        }
    };

    const handleFindPapers = () => {
        router.push("/discover");
    };

    return (
        <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                <QuickActionCard
                    icon={<Upload className="h-5 w-5" />}
                    title="Upload Paper"
                    description="Start your next study"
                    onClick={handleUploadClick}
                    variant="primary"
                />
                <QuickActionCard
                    icon={<FolderPlus className="h-5 w-5" />}
                    title="New Project"
                    description="Organize your research"
                    onClick={() => setCreateProjectOpen(true)}
                />
                <QuickActionCard
                    icon={<Globe2 className="h-5 w-5" />}
                    title="Discover Research"
                    description="Find relevant research from academic sources"
                    onClick={handleFindPapers}
                />
            </div>

            <UploadModal
                open={isUploadModalOpen}
                uploadLimit={1}
                onOpenChange={setUploadModalOpen}
                onUploadComplete={handleUploadComplete}
                onUploadStart={onUploadStart}
                onUrlImportStart={onUrlImportStart}
            />

            <CreateProjectDialog
                open={isCreateProjectOpen}
                onOpenChange={setCreateProjectOpen}
                onSubmit={handleCreateProject}
            />
        </>
    );
}
